import { addDays, eachDayOfInterval, format, parseISO } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

export function formatOptions<
  S extends { elements: T[] },
  T extends { id: string; name: string }
>(
  actionResult: S
): S & {
  byId: { [id: string]: T };
  options: { value: string; label: string }[];
} {
  let byId: { [id: string]: T } = {},
    options = [];
  for (const element of actionResult.elements) {
    byId[element.id] = element;
    options.push({ value: element.id, label: element.name });
  }
  return {
    ...actionResult,
    byId,
    options
  };
}

export function parseApiDateStr(dateStr: string, timeZone: string): Date {
  const parsedStr = dateStr.endsWith('Z') ? dateStr.slice(0, -1) : dateStr;

  // Convert to UTC forcing the zone
  const utcDate = fromZonedTime(parseISO(parsedStr), timeZone);

  // Convert time back to the specified timezone
  return toZonedTime(utcDate, timeZone);
}

/** Expands a query's `date` + day-count into the list of calendar days it
 *  covers (as 'yyyy-MM-dd'), e.g. for feeding `getDaysWithinTimespan` the
 *  window an open-ended schedule should be indexed under. Pure calendar-day
 *  arithmetic on the date string — no timezone conversion, since `date` is
 *  already a plain calendar date, not an instant. */
export function expandQueryDays(date: string, dayCount: number): string[] {
  const start = parseISO(date);
  return Array.from({ length: Math.max(dayCount, 1) }, (_, i) =>
    format(addDays(start, i), 'yyyy-MM-dd')
  );
}

export function getDaysWithinTimespan(
  startTime: string,
  endTime: string | null | undefined,
  timeZone: string,
  // The days actually being queried (as 'yyyy-MM-dd'), i.e. the caller's
  // own window — used only for an open-ended schedule, which has no end
  // to compute a span from otherwise. Comparing against "now" instead
  // would depend on whatever clock/timezone happens to be evaluating this,
  // which can disagree with the day the caller actually asked about.
  queryDays?: string[]
): string[] {
  // Open-ended schedules (e.g. a walk-up check-in with no end until
  // checkout) are still ongoing, not just on the day they started — a
  // check-in from two days ago with no checkout yet must still index under
  // today, or viewing today shows the resource as available. The backend
  // already decided this entry is relevant to the queried window (it's in
  // the response); index it under every requested day on/after its start,
  // trusting that decision rather than re-deriving one from a clock. A
  // caller iterating many schedules to build a shared index must never let
  // one open-ended entry throw and drop the whole batch — without a
  // window, fall back to indexing under its start day only.
  if (!endTime) {
    const startInTimeZone = parseApiDateStr(startTime, timeZone);
    const startDay = format(startInTimeZone, 'yyyy-MM-dd');
    if (!queryDays) {
      return [startDay];
    }
    return queryDays.filter((day) => day >= startDay);
  }

  // Convert start and end dates to the specified timezone
  const startInTimeZone = parseApiDateStr(startTime, timeZone);
  const endInTimeZone = parseApiDateStr(endTime, timeZone);

  // Set the time to midnight for both start and end dates
  // If the end date is midnight, subtract one day. We don't want to consider the day where the end time is midnight
  startInTimeZone.setHours(0, 0, 0, 0);
  if (
    endInTimeZone.getHours() === 0 &&
    endInTimeZone.getMinutes() === 0 &&
    endInTimeZone.getSeconds() === 0
  ) {
    endInTimeZone.setDate(endInTimeZone.getDate() - 1);
  }
  endInTimeZone.setHours(0, 0, 0, 0);

  // If start and end dates are the same, return the single day
  if (startInTimeZone.getTime() === endInTimeZone.getTime()) {
    return [format(startInTimeZone, 'yyyy-MM-dd')];
  }

  // Get all days within the timespan using date-fns
  const days = eachDayOfInterval({
    start: startInTimeZone,
    end: endInTimeZone
  });

  // Format the dates as strings
  const formattedDays = days.map((day) => format(day, 'yyyy-MM-dd'));

  return formattedDays;
}
